export type IntelligenceServiceCode = 'S-05' | 'S-06' | 'S-07' | 'S-12';
export type IntelligenceServiceIntake = Record<string, string>;

export const INTELLIGENCE_SERVICE_FIELDS: Record<IntelligenceServiceCode, Array<{ key: string; label: string; required?: boolean; kind?: 'date' | 'time' | 'textarea' | 'country' }>> = {
    'S-05': [
        { key: 'country', label: 'Country of analysis', required: true, kind: 'country' },
        { key: 'decisionContext', label: 'Decision or mission this analysis supports', required: true, kind: 'textarea' },
        { key: 'keyQuestions', label: 'Key intelligence questions', required: true, kind: 'textarea' },
        { key: 'clientAssets', label: 'Relevant people, sites, or operations', kind: 'textarea' },
        { key: 'timeHorizon', label: 'Planning horizon' },
    ],
    'S-06': [
        { key: 'groupName', label: 'Threat group or actor', required: true },
        { key: 'country', label: 'Country of concern', required: true, kind: 'country' },
        { key: 'clientExposure', label: 'People, assets, or operations at risk', required: true, kind: 'textarea' },
        { key: 'keyQuestions', label: 'Key intelligence questions', required: true, kind: 'textarea' },
        { key: 'knownInformation', label: 'Known incidents or source leads', kind: 'textarea' },
    ],
    'S-07': [
        { key: 'country', label: 'Daily micro report country', required: true, kind: 'country' },
    ],
    'S-12': [
        { key: 'principal_name', label: 'Principal name', required: true },
        { key: 'principal_title', label: 'Principal title or role' },
        { key: 'operation_name', label: 'Operation name' },
        { key: 'country', label: 'Movement country', required: true, kind: 'country' },
        { key: 'movement_date', label: 'Movement date', required: true, kind: 'date' },
        { key: 'movement_time', label: 'Movement time', kind: 'time' },
        { key: 'origin_location', label: 'Origin', required: true },
        { key: 'destination', label: 'Destination', required: true },
        { key: 'movement_type', label: 'Movement type', required: true },
        { key: 'route_primary', label: 'Planned primary route', kind: 'textarea' },
        { key: 'threat_actors', label: 'Known threat actors', kind: 'textarea' },
        { key: 'recent_incidents', label: 'Known recent incidents', kind: 'textarea' },
        { key: 'person_of_concern', label: 'Persons of concern', kind: 'textarea' },
        { key: 'detail_size', label: 'Protection detail size' },
        { key: 'vehicle_count', label: 'Vehicle count' },
        { key: 'medical_officer', label: 'Medical lead' },
        { key: 'special_instructions', label: 'Special instructions', kind: 'textarea' },
    ],
};

export const isIntelligenceService = (code: string): code is IntelligenceServiceCode =>
    Object.prototype.hasOwnProperty.call(INTELLIGENCE_SERVICE_FIELDS, code);

export function cleanIntelligenceIntake(code: IntelligenceServiceCode, source: unknown): IntelligenceServiceIntake {
    const raw = source && typeof source === 'object' ? source as Record<string, unknown> : {};
    const result: IntelligenceServiceIntake = {};
    for (const field of INTELLIGENCE_SERVICE_FIELDS[code])
        result[field.key] = String(raw[field.key] ?? '').trim().slice(0, field.kind === 'textarea' ? 3000 : 240);
    return result;
}

export function missingIntelligenceIntake(code: IntelligenceServiceCode, intake: IntelligenceServiceIntake): string[] {
    return INTELLIGENCE_SERVICE_FIELDS[code].filter(field => field.required && !intake[field.key]?.trim()).map(field => field.label);
}

export function intelligenceReportReady(code: IntelligenceServiceCode, report: any): boolean {
    if (code === 'S-07')
        return false;
    if (code === 'S-05')
        return report?.reportType === 'GeopoliticalAnalysis' &&
            [report.summary, report.executiveSummary, report.riskSummary, report.description]
                .some(value => typeof value === 'string' && Boolean(value.trim()));
    if (code === 'S-06')
        return report?.reportType === 'GlobalThreatGroupAnalysis' && Boolean(String(report.summary || '').trim())
            && Array.isArray(report.additionalSections) && report.additionalSections.length >= 10;
    return report?.reportType === 'EpPIBriefResult' && Boolean(String(report.overallAssessment || '').trim())
        && Boolean(String(report.routeStatus || '').trim()) && Boolean(String(report.contingencyActions || '').trim())
        && Array.isArray(report.protectiveMeasures) && report.protectiveMeasures.length >= 3;
}
